/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import _endsWithInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/ends-with";
import _Math$sign from "@babel/runtime-corejs3/core-js-stable/math/sign";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _slicedToArray from "@babel/runtime-corejs3/helpers/esm/slicedToArray";

/** @module react/components/trackless-slide */
// TODO: ADD OPTION TO AVOID INITIAL ANIMATION.
// TODO: ADD OPTION TO NOT ADD INTERNAL DIMENSION STYLES.
// TODO: MAKE next AND previous CALL goTo? TO AVOID CONFUSIONS. (TO CHANGE STATE ONLY IN ONE PLACE)
// TODO: IMPLEMENT DEFINITIVE KEY SYSTEM (MAKE IT WORK FOR ANY NUMBER OF CHILDREN).
// TODO: ADD DRAG SUPPORT VIA draggable BOOLEAN PROP (ONLY IF POSSIBLE) (USE react-use-gesture).
// TODO: ADD swipe SUPPORT IF DRAG SUPPORT IS NOT POSSIBLE.
import { arrayUtils, SEEDS } from '@codistica/core';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { animated, useTransition } from 'react-spring';
import resetClassNames from '../../css/reset.module.scss';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import componentClassNames from './index.module.scss';

/**
 * @typedef tracklessSlideDimensionsType
 * @property {(string|number)} height - Slide height.
 * @property {(string|number)} width - Slide width.
 * @property {(string|number)} [minHeight=null] - Slide minimum height.
 * @property {(string|number)} [minWidth=null] - Slide minimum width.
 * @property {(string|number)} [maxHeight=null] - Slide maximum height.
 * @property {(string|number)} [maxWidth=null] - Slide maximum width.
 */

/**
 * @typedef tracklessSlidePropsType
 * @property {string} [direction='row'] - Slide direction.
 * @property {number} [startingPosition=0] - Slide starting position.
 * @property {number} [itemsPerView=1] - Slide items per view.
 * @property {tracklessSlideDimensionsType} [dimensions] - Slide dimensions.
 * @property {boolean} [emulateTrack=false] - Emulate a track by rendering all children.
 * @property {Function} [onMount=null] - Callback for componentDidMount event.
 * @property {Function} [onNewPosition=null] - Callback for newPosition event.
 * @property {*} [children=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A simple yet powerful trackless slide component.
 * @param {tracklessSlidePropsType} props - Component props.
 * @returns {Object<string,*>} React component.
 */
function TracklessSlide(props) {
  var _this = this,
      _context,
      _context3,
      _context5,
      _context7,
      _context8,
      _context9,
      _context10,
      _context12,
      _context13,
      _context14,
      _context15;

  var direction = props.direction,
      startingPosition = props.startingPosition,
      itemsPerView = props.itemsPerView,
      dimensions = props.dimensions,
      emulateTrack = props.emulateTrack,
      onMount = props.onMount,
      onNewPosition = props.onNewPosition,
      children = props.children,
      style = props.style,
      className = props.className,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      globalTheme = props.globalTheme;

  var _ref = dimensions || {},
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 100 : _ref$height,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 100 : _ref$width,
      _ref$minHeight = _ref.minHeight,
      minHeight = _ref$minHeight === void 0 ? null : _ref$minHeight,
      _ref$minWidth = _ref.minWidth,
      minWidth = _ref$minWidth === void 0 ? null : _ref$minWidth,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? null : _ref$maxHeight,
      _ref$maxWidth = _ref.maxWidth,
      maxWidth = _ref$maxWidth === void 0 ? null : _ref$maxWidth;

  var _useState = useState({
    position: startingPosition,
    delta: itemsPerView,
    previousViewArray: []
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var previousViewArrayRef = useRef([]);
  var isRow = direction === 'row';
  var goTo = useCallback(_bindInstanceProperty(_context = function _context(newPosition) {
    var _this2 = this,
        _context2;

    _newArrowCheck(this, _this);

    return setState(_bindInstanceProperty(_context2 = function _context2(prevState) {
      _newArrowCheck(this, _this2);

      var delta = arrayUtils.getShortestPath(children, prevState.position, newPosition);
      newPosition = (prevState.position + delta) % children.length;
      onNewPosition && onNewPosition(newPosition);
      return {
        position: newPosition,
        delta: delta,
        previousViewArray: previousViewArrayRef.current
      };
    }).call(_context2, this));
  }).call(_context, this), [children.length]);
  var next = useCallback(_bindInstanceProperty(_context3 = function _context3(group) {
    var _this3 = this,
        _context4;

    _newArrowCheck(this, _this);

    return setState(_bindInstanceProperty(_context4 = function _context4(prevState) {
      _newArrowCheck(this, _this3);

      var delta = group ? itemsPerView : 1;
      var newPosition = (prevState.position + delta) % children.length;
      onNewPosition && onNewPosition(newPosition);
      return {
        position: newPosition,
        delta: delta,
        previousViewArray: previousViewArrayRef.current
      };
    }).call(_context4, this));
  }).call(_context3, this), [itemsPerView, children.length]);
  var previous = useCallback(_bindInstanceProperty(_context5 = function _context5(group) {
    var _this4 = this,
        _context6;

    _newArrowCheck(this, _this);

    return setState(_bindInstanceProperty(_context6 = function _context6(prevState) {
      _newArrowCheck(this, _this4);

      var delta = group ? -itemsPerView : -1;
      var newPosition = (prevState.position + delta) % children.length;
      newPosition = newPosition >= 0 ? newPosition : children.length + newPosition;
      onNewPosition && onNewPosition(newPosition);
      return {
        position: newPosition,
        delta: delta,
        previousViewArray: previousViewArrayRef.current
      };
    }).call(_context6, this));
  }).call(_context5, this), [itemsPerView, children.length]);
  var getIndexFromKey = useCallback(_bindInstanceProperty(_context7 = function _context7(array, key) {
    _newArrowCheck(this, _this);

    for (var i = 0; i < array.length; i++) {
      if (array[i].key === key || array[i].key === key + 'X') {
        return i;
      }
    }

    return null;
  }).call(_context7, this), []);
  var getStringKey = useCallback(_bindInstanceProperty(_context8 = function _context8(input) {
    _newArrowCheck(this, _this);

    return SEEDS.alphaLow.charAt(input);
  }).call(_context8, this), []);
  var generateViewArray = useCallback(_bindInstanceProperty(_context9 = function _context9(position) {
    _newArrowCheck(this, _this);

    var output = [];
    var slot = -Math.floor((children.length - itemsPerView) / 2);
    var key = (position + slot) % children.length;
    var index = 0;

    if (key < 0) {
      key += children.length;
    }

    while (index < children.length) {
      if (emulateTrack || slot >= 0 && slot < itemsPerView) {
        output.push({
          elem: children[key],
          key: getStringKey(key),
          slot: slot
        });
      }

      slot++;
      key = (key + 1) % children.length;
      index++;
    }

    return output;
  }).call(_context9, this), [children.length, itemsPerView, emulateTrack]);
  var getCurrentViewArray = useCallback(_bindInstanceProperty(_context10 = function _context10() {
    _newArrowCheck(this, _this);

    var newViewArray = generateViewArray(state.position);

    if (!emulateTrack) {
      return newViewArray;
    }

    var forward = _Math$sign(state.delta) !== -1;
    var probeIndex = getIndexFromKey(state.previousViewArray, newViewArray[forward ? 0 : newViewArray.length - 1].key);

    if (typeof probeIndex !== 'number') {
      probeIndex = forward ? 0 : newViewArray.length - 1;
    }

    var equivalentIndex = probeIndex;

    for (var index = forward ? 0 : newViewArray.length - 1; forward ? index < newViewArray.length : index >= 0; index += forward ? 1 : -1) {
      if (state.previousViewArray[probeIndex]) {
        newViewArray[index].key = state.previousViewArray[probeIndex].key;
      } else {
        if (state.previousViewArray[equivalentIndex]) {
          var _context11;

          newViewArray[index].key = _endsWithInstanceProperty(_context11 = state.previousViewArray[equivalentIndex].key).call(_context11, 'X') ? state.previousViewArray[equivalentIndex].key.replace('X', '') : state.previousViewArray[equivalentIndex].key + 'X';
        }
      }

      probeIndex += forward ? 1 : -1;
      equivalentIndex = (equivalentIndex + (forward ? 1 : -1)) % children.length;

      if (equivalentIndex < 0) {
        equivalentIndex += state.previousViewArray.length;
      }
    }

    return newViewArray;
  }).call(_context10, this), [generateViewArray, state.position, state.delta, state.previousViewArray]);
  var getTransformValue = useCallback(_bindInstanceProperty(_context12 = function _context12(value) {
    _newArrowCheck(this, _this);

    if (isRow) {
      return "translateX(".concat(value, "%)");
    } else {
      return "translateY(".concat(value, "%)");
    }
  }).call(_context12, this), [isRow]);
  var globalStyles = globalTheme ? TracklessSlide.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? TracklessSlide.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles(globalStyles.root, customStyles.root, style, {
      height: height,
      width: width,
      minHeight: minHeight,
      minWidth: minWidth,
      maxHeight: maxHeight,
      maxWidth: maxWidth
    }),
    item: mergeStyles(globalStyles.item, customStyles.item, {
      height: !isRow ? "".concat(100 / itemsPerView, "%") : '100%',
      width: isRow ? "".concat(100 / itemsPerView, "%") : '100%',
      minHeight: null,
      minWidth: null,
      maxHeight: null,
      maxWidth: null
    })
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, componentClassNames.root, globalClassNames.root, customClassNames.root, className),
    item: mergeClassNames(componentClassNames.item, globalClassNames.item, customClassNames.item)
  };
  var transitions = useTransition(previousViewArrayRef.current = getCurrentViewArray(), _bindInstanceProperty(_context13 = function _context13(i) {
    _newArrowCheck(this, _this);

    return i.key;
  }).call(_context13, this), {
    unique: false,

    /**
     * @description Callback for from event.
     * @param {Object} arg - Arg.
     * @param {number} arg.slot - Item slot.
     * @returns {Object<string,*>} Props.
     */
    from: function from(_ref2) {
      var slot = _ref2.slot;

      var sign = _Math$sign(state.delta);

      var multiplier = emulateTrack ? Math.abs(state.delta) : Math.min(Math.abs(state.delta), itemsPerView);
      return {
        transform: getTransformValue(100 * (slot + sign * multiplier))
      };
    },

    /**
     * @description Callback for enter event.
     * @param {Object} arg - Arg.
     * @param {number} arg.slot - Item slot.
     * @returns {Object<string,*>} Props.
     */
    enter: function enter(_ref3) {
      var slot = _ref3.slot;
      return {
        transform: getTransformValue(slot * 100)
      };
    },

    /**
     * @description Callback for leave event.
     * @param {Object} arg - Arg.
     * @param {number} arg.slot - Item slot.
     * @returns {Object<string,*>} Props.
     */
    leave: function leave(_ref4) {
      var slot = _ref4.slot;

      var sign = _Math$sign(state.delta);

      var multiplier = emulateTrack ? Math.abs(state.delta) : Math.min(Math.abs(state.delta), itemsPerView);
      return {
        transform: getTransformValue(100 * (slot - sign * multiplier))
      };
    },

    /**
     * @description Callback for update event.
     * @param {Object} arg - Arg.
     * @param {number} arg.slot - Item slot.
     * @returns {Object<string,*>} Props.
     */
    update: function update(_ref5) {
      var slot = _ref5.slot;
      return {
        transform: getTransformValue(slot * 100)
      };
    }
  });
  useEffect(_bindInstanceProperty(_context14 = function _context14() {
    _newArrowCheck(this, _this);

    onMount && onMount({
      goTo: goTo,
      next: next,
      previous: previous
    });
  }).call(_context14, this), []);
  return /*#__PURE__*/React.createElement("div", {
    style: mergedStyles.root,
    className: mergedClassNames.root
  }, _mapInstanceProperty(transitions).call(transitions, _bindInstanceProperty(_context15 = function _context15(transition) {
    _newArrowCheck(this, _this);

    return /*#__PURE__*/React.createElement(animated.div, {
      key: transition.key,
      style: mergeStyles(mergedStyles.item, transition.props),
      className: mergedClassNames.item
    }, transition.item.elem);
  }).call(_context15, this)));
}

TracklessSlide.defaultProps = {
  direction: 'row',
  startingPosition: 0,
  itemsPerView: 1,
  emulateTrack: false,
  onMount: null,
  onNewPosition: null,
  children: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
TracklessSlide.globalStyles = {
  default: {
    root: {},
    item: {}
  }
};
TracklessSlide.globalClassNames = {
  default: {
    root: '',
    item: ''
  }
};
export { TracklessSlide };